import Dexie from 'dexie'
import type { EntityTable } from 'dexie'
import type { Dostawca, Kategoria, Klient, KursWaluty, Platnosc, Pozycja } from './typy.js'

/** Pojedyncza wartość konfiguracyjna, np. kursor synchronizacji. */
export interface Meta {
  klucz: string
  wartosc: string
}

export type NazwaTabeli = 'items' | 'payments' | 'categories' | 'vendors' | 'clients'

/**
 * Migawka rekordu: albo jego treść, albo `null` oznaczające „ma nie istnieć".
 * Tym samym kształtem opisujemy stan sprzed operacji (do cofnięcia) i stan
 * po niej (do ponownego nałożenia po pobraniu danych z serwera).
 */
export interface Migawka {
  tabela: NazwaTabeli
  id: string
  rekord: unknown | null
}

export type Cofniecie = Migawka

/**
 * Operacja czekająca na wysłanie. `id` trafia do nagłówka `X-Operation-Id`,
 * więc powtórzenie po zerwaniu połączenia nie zdubluje kosztu.
 */
export interface WpisKolejki {
  id: string
  metoda: 'POST' | 'PATCH' | 'DELETE'
  sciezka: string
  body: unknown
  cofniecie: Migawka[]
  /** Stan, jaki operacja nadała rekordom — nakładany ponownie po każdym pobraniu. */
  skutek: Migawka[]
  /** Identyfikatory lokalnego szkicu harmonogramu, który ustąpi wersji serwerowej. */
  szkicPlatnosci: string[]
  utworzono: string
  proby: number
  ostatniBlad: string | null
}

/**
 * Lokalna kopia danych. Interfejs czyta wyłącznie stąd, dzięki czemu każdy
 * ekran rysuje się natychmiast i działa bez sieci. Serwer pozostaje źródłem
 * prawdy — wchodzi tu przez `pull()`.
 */
export class BazaLokalna extends Dexie {
  items!: EntityTable<Pozycja, 'id'>
  payments!: EntityTable<Platnosc, 'id'>
  categories!: EntityTable<Kategoria, 'id'>
  vendors!: EntityTable<Dostawca, 'id'>
  clients!: EntityTable<Klient, 'id'>
  fxRates!: EntityTable<KursWaluty, 'currency'>
  meta!: EntityTable<Meta, 'klucz'>
  outbox!: EntityTable<WpisKolejki, 'id'>

  constructor(nazwa = 'apka-firma') {
    super(nazwa)
    this.version(1).stores({
      items: 'id, cadence, type, categoryId, title',
      payments: 'id, itemId, dueDate, status, [itemId+dueDate]',
      categories: 'id, name',
      vendors: 'id, name',
      clients: 'id, name',
      fxRates: '[currency+date], currency, date',
      meta: 'klucz',
    })
    // Kolejka operacji czekających na wysłanie — podstawa pracy bez sieci.
    this.version(2).stores({ outbox: 'id, utworzono' })
  }
}

export const db = new BazaLokalna()

let zglosZablokowanie: (() => void) | null = null

/** Pozwala interfejsowi pokazać, że baza czeka na zamknięcie innej karty. */
export function ustawRaportZablokowania(fn: (() => void) | null): void {
  zglosZablokowanie = fn
}

/**
 * Obsługa zmiany schematu między kartami.
 *
 * Bez tego aktualizacja schematu potrafi zawisnąć: starsza karta trzyma otwarte
 * połączenie, nowa czeka na przebudowę, a każde `await db...` w nowej karcie
 * nigdy nie wraca — bez błędu i bez wyjątku. Na ekranie wygląda to jak przycisk,
 * który został wciśnięty i już nic nie robi.
 */
db.on('versionchange', () => {
  // Inna karta chce podnieść schemat. Ustępujemy jej miejsca i wracamy
  // z nowym kodem, zamiast blokować ją w nieskończoność.
  db.close()
  window.location.reload()
})

db.on('blocked', () => {
  zglosZablokowanie?.()
})

export const KLUCZ_KURSORA = 'kursor-sync'

export async function odczytajMeta(klucz: string): Promise<string | undefined> {
  return (await db.meta.get(klucz))?.wartosc
}

export async function zapiszMeta(klucz: string, wartosc: string): Promise<void> {
  await db.meta.put({ klucz, wartosc })
}

/** Czyści całą kopię lokalną — używane przy wylogowaniu. */
export async function wyczysc(): Promise<void> {
  await Promise.all([
    db.items.clear(),
    db.payments.clear(),
    db.categories.clear(),
    db.vendors.clear(),
    db.clients.clear(),
    db.fxRates.clear(),
    db.meta.clear(),
    db.outbox.clear(),
  ])
}
